import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';

import { toSlug } from 'utils';
import { trackClick } from 'utils/AnalyticsUtils';
import { local12Hour } from 'utils/LocalDateTimeHelper';
import { preventLinkScroll } from 'utils/commonUtils';

import { LocalTimeShort } from 'modules/Common/components/LocalDateTime';
import CollapsibleSection from 'modules/Common/components/CollapsibleSection';
import RaceSummaryText from 'modules/Common/components/RaceSummaryText';
import CaretIcon from 'modules/Common/components/svg/CaretIcon';
import CaretLink from 'modules/Common/components/CaretLink';
import RaceJSONLD from 'modules/Common/components/RaceJSONLD';

import SubscriptionContainer from 'modules/Common/containers/Subscriptions/SubscriptionContainer';
import StarButton from 'modules/Common/containers/Subscriptions/StarButton';
import { SUBSCRIPTIONS_HORSE_RACE } from 'modules/Common/consts';

import {isBeforeOffStatus} from 'modules/HorseRacing/utils';
import PromoIcon from 'modules/Common/components/svg/PromoIcon';
import {titleToId} from 'utils/url-rewrite';

const HORSE_RACE_COMPLETE_STATUSES = ['WEIGHEDIN', 'RESULT','FINISHED'];

const RACE_ENDED_STATUSES = HORSE_RACE_COMPLETE_STATUSES.concat(['ABANDONED']);

export default function RacecardsMeetingSummary(props) {
  const {
    "result-racecard": resultRacecard,
    meeting,
    meeting: {
      meeting_summary: {
        status: meetingStatus = "",
        abandoned: abandonedReason = "",
        inspection: {
          time: inspectionTime = ""
        } = {},
        course: {
          name: courseName = 'unknown',
          feed_source: feedSource
        } = {},
        going: meetingGoing = ' - ',
        surface_summary: meetingSurface = ' - ',
        meeting_reference: {
          id: meetingId
        } = {}
      } = {},
      races = []
  } = {},
    date: meetingDate,
    dateIncrement
  } = props;

  const courseNameSlug = toSlug(courseName);

  const formattedInspectionTime = local12Hour(inspectionTime);

  const racecardUrl = (raceName, raceId) =>
    `/racing/racecards/${meetingDate}/${courseNameSlug}/racecard/${raceId}/${toSlug(raceName)}`;

  const resultUrl = (raceName, raceId) =>
    `/racing/results/${meetingDate}/${courseNameSlug}/${raceId}/${toSlug(raceName)}`;

  let type = (resultRacecard === 'result')?'result':'racecard';
  function racecardHref(raceName = 'unknown', raceId) {
    if (resultRacecard == "result") {
      return resultUrl(raceName, raceId);
    }
    else {
      return racecardUrl(raceName, raceId);
    }
  }

  const courseTitle = meetingStatus === "ABANDONED" ?
    `${courseName} (OFF)` :
    courseName;

  let goingDescription = meetingGoing;

  // strip out any inspection text if inspection is already flagged in statue
  if (meetingStatus === "ABANDONED" && meetingGoing.toLowerCase().indexOf('(inspection')) {
    goingDescription = meetingGoing.replace(/\(.*\)/,"");
  }

  function resultHref(raceName = 'unknown', raceId) {
    return resultUrl(raceName, raceId);
  }

  const meetingPageUrl = `/racing/meeting/${meetingDate}/${courseNameSlug}/${meetingId}`;

  function isRaceAbandoned(raceStage, meetingStatus) {
    return raceStage === "ABANDONED" ||
      (meetingStatus === "ABANDONED" && raceStage === "DORMANT");
  }

  return (
    <section className="hr-meeting-container" id={titleToId(courseName)}>
      <RaceJSONLD {...{meeting}} sport='racing' type={type}/>
      { dateIncrement === 1 ?
        <CollapsibleSection feedLogo={ feedSource } title={ courseTitle } linkTo={ meetingPageUrl }>
          <div className="hr-meeting-meta">
            <div className="hr-meeting-meta-going">
            { meetingStatus === "ABANDONED" &&
              <div>
                <span className="hr-meeting-meta-label">Abandoned: </span>
                <span className="hr-meeting-meta-value">{ abandonedReason }</span>
              </div>
              }
              <span className="hr-meeting-meta-label">Going: </span>
              <span className="hr-meeting-meta-value">{ goingDescription }</span>
              { meetingStatus === "INSPECTION" &&
                formattedInspectionTime !== "" &&
              <div>
                <span className="hr-meeting-meta-label">Inspection: </span>
                <span className="hr-meeting-meta-value">{ formattedInspectionTime }</span>
              </div>
              }
            </div>
            <div className="hr-meeting-meta-surface">
              <strong>Surface: </strong>
              <span className="hr-meeting-meta-value">{ meetingSurface }</span>
            </div>
            <div className="hr-meeting-meta-fast-cards">
              <CaretLink to={`/racing/fast-cards/${meetingId}/${meetingDate}/${courseNameSlug}`}>Fast Cards</CaretLink>
            </div>
            { !races.every(({ race_stage }) => RACE_ENDED_STATUSES.includes(race_stage)) &&
              meetingStatus !== "ABANDONED" &&
              <div className="hr-meeting-all-follow">
                <SubscriptionContainer
                  addSubscriptions={
                    races.map(({
                      race_summary_reference: {
                        id: raceId
                      } = {},
                      time: raceTime
                    }) => ({
                      type: SUBSCRIPTIONS_HORSE_RACE,
                      id: raceId,
                      text: `${raceTime} ${courseName}`
                    }))
                  }
                  icon={
                    <StarButton
                      trackClick={trackClick("followMeeting", { meetingId }, true)}
                      selectedTitle="Unfollow All Races"
                      deselectedTitle="Follow All Races"
                    />
                  }
                />
              </div>
            }
          </div>
          <ul className="hr-meeting-races-container">
            {
              races.map(race => {
                const {
                  race_summary_reference: {
                    id: raceId
                  } = {},
                  promotion: {
                    image: promoIcon
                  } = {},
                  date: raceDate,
                  time: raceTime,
                  name: raceName = 'unknown',
                  race_stage: raceStage
                } = race;

                const showPromo = !!(promoIcon && isBeforeOffStatus(raceStage));

                return (
                  <li key={ raceId } id="race-container">
                    <Link to={racecardHref(raceName, raceId)}>
                      <LocalTimeShort className="hr-meeting-race-time" iso={`${raceDate}T${raceTime}Z`} />
                      <div className="hr-meeting-race-name-star" id="hr-meeting-race-name">
                        { raceName } <RaceSummaryText raceSummary={race}/>
                      </div>
                      <div className="hr-meeting-race-promo">
                        {showPromo && <div className="hr-meeting-race-promo-icon"><PromoIcon type={promoIcon}/></div>}
                      </div>
                    </Link>
                    { isRaceAbandoned(raceStage, meetingStatus) ?
                      <div className="hr-meeting-race-abandoned">
                        <span className="hr-meeting-race-abandoned-text">Abd.</span>
                      </div> :
                      HORSE_RACE_COMPLETE_STATUSES.includes(raceStage) ?
                        <div className="hr-meeting-race-result-fulllink">
                           <Link className="hr-meeting-race-result-fulllink-btn" to={resultHref(raceName, raceId)}>Result</Link>
                        </div>
                      :
                        <div className="hr-meeting-race-follow">
                          <SubscriptionContainer
                            addSubscriptions={ [{
                              type: SUBSCRIPTIONS_HORSE_RACE,
                              id: raceId,
                              text: `${raceTime} ${courseName}`
                            }] }
                            icon={
                              <StarButton
                                trackClick={trackClick("followRace", { raceId }, true)}
                                selectedTitle="Unfollow"
                                deselectedTitle="Follow"
                              />
                            }
                          />
                        </div>
                      }
                  </li>
                );
              })
            }
          </ul>
        </CollapsibleSection>
        :
        // Results
        <div className="dividerRow">
          <div className="divider-title">
            <div className="divider-title-text">
              <span><Link to={`/racing/meeting/${meetingDate}/${courseNameSlug}/${meetingId}`}>{ courseTitle }</Link></span>
            </div>
            <div className="divider-subtitle">
              <Link to={`/racing/meeting/${meetingDate}/${courseNameSlug}/${meetingId}`}><CaretIcon type="full" direction="right"/>&nbsp;Fast Results</Link>
            </div>
          </div>
          <div className="hr-meeting-meta">
            <div className="hr-meeting-meta-going-no-star">
              { meetingStatus === "ABANDONED" &&
                <div>
                  <span className="hr-meeting-meta-label">Abandoned: </span>
                  <span className="hr-meeting-meta-value">{ abandonedReason }</span>
                </div>
              }
              <span className="hr-meeting-meta-label">Going: </span>
              <span className="hr-meeting-meta-value">{ meetingGoing }</span>
            </div>
            <div className="hr-meeting-meta-surface">
              <strong>Surface: </strong>
              <span className="hr-meeting-meta-value">{ meetingSurface }</span>
            </div>
          </div>
          <ul className="hr-meeting-races-container">
            {
              races.map(({
                race_summary_reference: {
                  id: raceId
                } = {},
                date: raceDate,
                time: raceTime,
                name: raceName = 'unknown',
                race_stage: raceStage,
                has_video: hasRaceVid
              }) => (
                <li key={raceId}>
                  <Link to={racecardHref(raceName, raceId)}>
                    <LocalTimeShort className="hr-meeting-race-time" iso={`${raceDate}T${raceTime}Z`} />
                    <div className={ hasRaceVid === true ? 'hr-meeting-race-name-result-with-video-icon' : 'hr-meeting-race-name-result' }>
                        { raceName }
                    </div>
                  </Link>
                  {
                    HORSE_RACE_COMPLETE_STATUSES.includes(raceStage) &&
                    <div className="hr-meeting-race-result-fulllink">
                      <Link className="hr-meeting-race-result-fulllink-btn" to={resultHref(raceName, raceId)}>Result</Link>
                    </div>
                  }
                  {
                      hasRaceVid === true &&
                      <div className="hr-meeting-race-result-video-icon">
                          <Link to={ resultHref(raceName, raceId) + '#video-player' }>
                              <img src={'/img/icons/video-icon.png'}/>
                          </Link>
                      </div>
                  }
                  { isRaceAbandoned(raceStage, meetingStatus) &&
                    <div className="hr-meeting-race-abandoned">
                      <span className="hr-meeting-race-abandoned-text">Abd.</span>
                    </div>
                  }
                </li>
              ))
            }
          </ul>
        </div>
      }
    </section>
  );
}

RacecardsMeetingSummary.propTypes = {
  "result-racecard": PropTypes.string,
  meeting: PropTypes.object.isRequired,
  date: PropTypes.string.isRequired,
  dateIncrement: PropTypes.number,
  toggleFollowed: PropTypes.func,
  toggleAllFollowed: PropTypes.func,
  checkFollowed: PropTypes.func,
  checkAnyFollowed: PropTypes.func
};



// WEBPACK FOOTER //
// ./modules/HorseRacing/components/HorseRacingRacecardsMeetingSummary.js